import { useState, useEffect, useCallback, useMemo } from 'react';
import { 
    useGetSelectFieldValues, 
    useGetProviderInfo
} from '@/shared/api/client/field-configuration-controller';
import type {
    GetSelectFieldValuesParams,
    SelectFieldOption,
    SelectFieldProviderInfo
} from '@/shared/api/model';

export interface UseDynamicSelectOptions {
    providerName: string;
    fieldName?: string;
    entityType?: string;
    categoryId?: number;
    apiBaseUrl?: string;
}

export interface UseDynamicSelectReturn {
    options: SelectFieldOption[];
    providerInfo: SelectFieldProviderInfo | null;
    loading: boolean;
    error: string | null;
    fetchOptions: (search?: string) => void;
    refetch: () => void;
}

export const useDynamicSelect = ({
    providerName,
    fieldName,
    entityType,
    categoryId
}: UseDynamicSelectOptions): UseDynamicSelectReturn => {
    const [searchQuery, setSearchQuery] = useState<string | undefined>(undefined);
    
    // Prepare parameters for the API call
    const params: GetSelectFieldValuesParams = useMemo(() => ({
        search: searchQuery,
        fieldName,
        entityType,
        categoryId
    }), [searchQuery, fieldName, entityType, categoryId]);

    // Use orval-generated hooks
    const {
        data: optionsResponse,
        isLoading: optionsLoading,
        error: optionsError,
        refetch: refetchOptions
    } = useGetSelectFieldValues(providerName, params, {
        query: {
            enabled: !!providerName,
            staleTime: 5 * 60 * 1000, // 5 minutes
            gcTime: 10 * 60 * 1000, // 10 minutes
        }
    });

    const {
        data: providerInfoResponse,
        isLoading: providerInfoLoading,
        error: providerInfoError
    } = useGetProviderInfo(providerName, {
        query: {
            enabled: !!providerName,
            staleTime: 30 * 60 * 1000, // 30 minutes - provider info doesn't change often
            gcTime: 60 * 60 * 1000, // 1 hour
        }
    });

    // Extract data from responses
    // Note: orval-generated hooks already unwrap the response data
    const options = useMemo(() => {
        return optionsResponse || [];
    }, [optionsResponse]);

    const providerInfo = useMemo(() => {
        return providerInfoResponse || null;
    }, [providerInfoResponse]);

    // Combine loading states
    const loading = optionsLoading || providerInfoLoading;

    // Handle errors
    const error = useMemo(() => {
        if (optionsError) {
            return `Failed to load options: ${optionsError instanceof Error ? optionsError.message : 'Unknown error'}`;
        }
        if (providerInfoError) {
            return `Failed to load provider info: ${providerInfoError instanceof Error ? providerInfoError.message : 'Unknown error'}`;
        }
        return null;
    }, [optionsError, providerInfoError]);

    // Fetch options with search query
    const fetchOptions = useCallback((search?: string) => {
        setSearchQuery(search);
    }, []);

    // Refetch function
    const refetch = useCallback(() => {
        refetchOptions();
    }, [refetchOptions]);

    return {
        options,
        providerInfo,
        loading,
        error,
        fetchOptions,
        refetch
    };
};
